<script>
    import { onDestroy, onMount, tick } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getDaysDifferFrom,
        getFormatDateAndTime,
        getInsuranceType,
        getText,
        goToURL,
        isObjectEmpty,
        replaceURL,
        shallowCopy,
        showError,
        showException,
        getUrlWithoutSearch,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getFormatDate,
        useTableScrollMemory,
    } from "../../Utils/utilFunctions.svelte";

    import {
        cacheInsuranceFunds,
        cacheTrafficAccidentAuthorities,
        cacheWorkersCompensation,
        startRefreshTable,
        stopRefreshTable,
    } from "../../Utils/utilCache.svelte";

    import {
        insuranceFund,
        isMobile,
        isUserStayOnTab,
        tacEncounterIdFullDetails,
        trafficAccidentAuthorities,
        user,
        wcEncounterIdFullDetails,
        workersCompensation,
    } from "../../stores";

    import {
        MultiSearchSelect,
        PageLoading,
        SearchInput,
        SvelteTable,
        SearchSelect,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import {
        EncounterConsentType,
        InsuranceType,
    } from "../../Utils/utilEnums.svelte";

    import { addIcon, eyeIcon } from "../../Utils/utilConstants.svelte";

    import {
        getEncounterWithWcAndTacDetails,
        getNameOfInsurer,
    } from "../FollowUp/helpers/followUpHelper.svelte";

    import EditNotesModal from "../FollowUp/EditNotesModal.svelte";

    import { syncEncounterLatestNote } from "../PatientEncounter/helpers/encounterHelper.svelte";

    import ViewNotesModal from "../FollowUp/ViewNotesModal.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import EncounterTableEncounterIdCell from "../PatientEncounter/components/EncounterTableEncounterIdCell.svelte";

    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    $: isLoading =
        isLoadingEncounter || isLoadingHealthFund || isLoadingAccountDoctors;

    $: if (!$isUserStayOnTab) stopRefreshTable();
    else
        refreshTableInterval = startRefreshTable(
            {
                tableIntervalRef: refreshTableInterval,
            },
            () => reloadPatientEncounter(),
        );

    let isLoadingEncounter = false,
        postEncounterData = [];

    let filters = { badDebtor: Number(0) },
        isLoadingAccountDoctors = false;

    let searchInsuranceTypeArr = [],
        searchPrivateHealthFundArr = [],
        isLoadingHealthFund = false,
        readyToShowMultiFilters = false, // render saved options properly
        healthFundOptionsArr = [],
        isShowPrivateHealthFundSelect = false;

    let searchWorkCoverAuthorityFundArr = [],
        workCoverAuthorityOptionsArr = [],
        isLoadingWorkCoverAuthorityFund = false,
        isShowWorkCoverAuthoritySelect = false;

    let searchTrafficAccidentFundArr = [],
        trafficAccidentOptionsArr = [],
        isLoadingTrafficAccidentAuthority = false,
        isShowTrafficAccidentSelect = false;

    let page = 0,
        pageSize = 25,
        count;

    let isEditNotesModalOpen = false,
        isViewNotesModalOpen = false,
        selectedEncounter = {};

    let selectedDoctors = [];

    let refreshTableInterval, scrollTableContainerRef;

    const { saveScroll, restoreScrollPosition } =
        useTableScrollMemory("ConsentDue");

    const sortableHeaderKeys = ["id", "doctor", "scheduledDate", "invoiceDate"];

    const defaultFilter = {
        "filter[scheduleDateTo]": getDaysDifferFrom(1),
        "filter[scheduleDateFrom]": getDaysDifferFrom(-30),
        "filter[estimateignoredrejected]": 1,
    };

    const ConsentDueFilter = {
        "filter[consent]": "1",
        "filter[billingStatus]": "1,2",
        "filter[excludedemoaccounts]": 1,
    };

    const columns = [
        {
            key: "id",
            title: "Enc. ID",
            headerClass:
                "text-left pl-3 sortable-header-decoration rounded-tl-xl",
            class: "px-3 text-left",
            renderComponent: {
                component: EncounterTableEncounterIdCell,
                props: {
                    clickable: true,
                },
            },
            csvContent: (v) => getText(v.id),
        },
        {
            key: "invoiceNumber",
            title: "Invoice No.",
            headerClass: "text-left pl-3",
            class: "px-3 text-left",
            renderValue: (v) => _getInvoiceId(v.invoices),
            csvContent: (v) => _getInvoiceId(v.invoices, true),
        },
        {
            key: "invoiceDate",
            title: "Invoice Due Date",
            headerClass: "text-left pl-3 sortable-header-decoration",
            class: "px-3 text-left",
            renderValue: (v) => _getInvoiceDueDate(v),
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass: "text-left pl-3 sortable-header-decoration",
            class: "px-3 text-left",
            renderValue: (v) => _getDoctorName(v.doctor),
            csvContent: (v) => _getDoctorName(v.doctor, true),
        },
        {
            key: "scheduledDate",
            title: "Scheduled Date",
            headerClass: "text-left pl-3 sortable-header-decoration",
            class: "px-3 text-left",
            renderValue: (v) => _getScheduleDate(v.scheduledDate),
        },
        {
            key: "insuranceType",
            title: "Insurance Type",
            headerClass: "text-left pl-3",
            class: "px-3 text-left",
            renderValue: (v) => _getInsuranceType(v.patient),
        },
        {
            key: "insuranceDetails",
            title: "Name of Insurer",
            headerClass: "text-left pl-3 w-1/5",
            class: "px-3 text-left",
            renderValue: (v) => getText(v.nameOfInsurer),
        },
        {
            key: "noteDate",
            title: "Last Note Date",
            headerClass: "text-left pl-3",
            class: "px-3 text-left",
            renderValue: (v) =>
                v.ltsNote ? getFormatDateAndTime(v.ltsNote.updatedDate) : "",
        },
        {
            key: "notes",
            title: "Last Note",
            headerClass: "text-left pl-3 w-1/5",
            class: "px-3 break-all text-left",
            renderValue: (v) => getText(v.ltsNote ? v.ltsNote.content : ""),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl",
            class: "px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(v.hasNotes),
            csvExportHidden: true,
        },
    ];

    onMount(async () => {
        getUrlWithoutSearch();

        await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        await loadPatientEncounters(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        readyToShowMultiFilters = true;
    });

    onDestroy(() => {
        stopRefreshTable(refreshTableInterval);

        $wcEncounterIdFullDetails = [];
        $tacEncounterIdFullDetails = [];
    });

    const updateSearchParamsByQuery = async (query) => {
        filters.dateFrom = defaultFilter["filter[scheduleDateFrom]"];
        filters.dateTo = defaultFilter["filter[scheduleDateTo]"];

        if (query) {
            const arr = decodeSearchParams(query).split("&");

            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("encounterId="))
                    filters.encounterId = arr[i].split("=")[1].toString();

                if (arr[i].includes("doctorId=")) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("patientInsuranceType=")) {
                    searchInsuranceTypeArr = arr[i]
                        .split("=")[1]
                        .split(",")
                        .map(Number);

                    handleInsuranceTypeChange(searchInsuranceTypeArr, {
                        requireReloadFunction: false,
                    });
                }

                if (arr[i].includes("patientInsuranceID="))
                    searchPrivateHealthFundArr = arr[i]
                        .split("=")[1]
                        .split(",");

                if (arr[i].includes("workCoverAuthorityID="))
                    searchWorkCoverAuthorityFundArr = arr[i]
                        .split("=")[1]
                        .split(",");

                if (arr[i].includes("trafficAccidentAuthorityID="))
                    searchTrafficAccidentFundArr = arr[i]
                        .split("=")[1]
                        .split(",");

                if (arr[i].includes("fromDate="))
                    filters.dateFrom = arr[i].split("=")[1];

                if (arr[i].includes("toDate="))
                    filters.dateTo = arr[i].split("=")[1];

                if (arr[i].includes("commsDisabled=1"))
                    filters.commsDisabled = true;

                if (arr[i].includes("badDebtor="))
                    filters.badDebtor = Number(arr[i].split("=")[1]);
                else filters.badDebtor = Number(-1);
            }

            updateURL();
        }
    };

    const loadPatientEncounters = async (start, limit, params) => {
        isLoadingEncounter = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getPatientEncounter(start, limit, params)
            .then(async (res) => await _handlePatientEncounters(res))
            .then(() => _updateHeaderSortIcon())
            .catch((err) => showException(err));

        isLoadingEncounter = false;
    };

    const reloadPatientEncounter = async () => {
        saveTableScroll();
        await apiMethods.getMethod
            .getPatientEncounter(page * pageSize, pageSize, _getSearchParams())
            .then(
                async (res) => await _handlePatientEncounters(res, false, true),
            )
            .then(() => _updateHeaderSortIcon())
            .then(() => restoreScrollPosition())
            .catch((err) => showException(err));
    };

    const saveTableScroll = () => {
        if (scrollTableContainerRef)
            saveScroll(
                scrollTableContainerRef.scrollTop,
                scrollTableContainerRef.scrollLeft,
            );
    };

    const restoreTableScroll = () => {
        if (!scrollTableContainerRef) return;

        scrollTableContainerRef.scrollTop = restoreScrollPosition().top;
        scrollTableContainerRef.scrollLeft = restoreScrollPosition().left;

        saveScroll();
    };

    const loadInsuranceFund = async () => {
        isLoadingHealthFund = true;

        await cacheInsuranceFunds();

        _updateInsuranceFundsOptions();

        isLoadingHealthFund = false;
    };

    const loadWorkCoverAuthorityFund = async () => {
        isLoadingWorkCoverAuthorityFund = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheWorkersCompensation();

        _updateWorkCoverOptions();

        isLoadingWorkCoverAuthorityFund = false;
    };

    const loadTrafficAccidentFund = async () => {
        isLoadingTrafficAccidentAuthority = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheTrafficAccidentAuthorities();

        _updateTrafficAccidentOptions();

        isLoadingTrafficAccidentAuthority = false;
    };

    const updateURL = () => {
        replaceURL(
            `/ConsentDue/page=${page}&size=${pageSize}&${_getParamsString()}`,
        );
    };

    const onPageSizeChange = (newSize) => {
        pageSize = newSize;
        page = 0;

        updateURL();

        loadPatientEncounters(0, pageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPatientEncounters(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (
            key == "id" ||
            key == "invoiceNumber" ||
            key == "doctor" ||
            key == "insuranceDetails"
        ) {
            const classArr = className.split(" ");

            if (className.includes("invoice")) {
                const targetClass = classArr.find((cls) =>
                    cls.includes("invoice"),
                );
                const invoiceId = targetClass.replace("invoice", "");
                goToURL(`/Invoices/view/${invoiceId}`);
            }
            if (className.includes("doctor")) {
                const targetClass = classArr.find((cls) =>
                    cls.includes("doctor"),
                );
                const doctorId = targetClass.replace("doctor", "");
                goToURL(`/Account/view/${doctorId}`);
            }
        } else if (key == "buttons") {
            if (className.includes("notesIcon")) _openEditNotesModal(rowData);
            else if (className.includes("viewIcon"))
                _openViewNotesModal(rowData);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadPatientEncounters(0, pageSize, _getSearchParams());
        }
    };

    const handleFilterSelect = () => {
        page = 0;
        updateURL();
        loadPatientEncounters(0, pageSize, _getSearchParams());
    };

    const handleInsuranceTypeChange = async (
        searchResult,
        { requireReloadFunction = true },
    ) => {
        searchInsuranceTypeArr = [...searchResult];

        if (!searchInsuranceTypeArr?.length) {
            isShowPrivateHealthFundSelect = false;
            searchPrivateHealthFundArr = [];

            isShowWorkCoverAuthoritySelect = false;
            searchWorkCoverAuthorityFundArr = [];

            isShowTrafficAccidentSelect = false;
            searchTrafficAccidentFundArr = [];
        } else {
            if (
                searchInsuranceTypeArr.length &&
                searchInsuranceTypeArr.includes(0)
            )
                isShowPrivateHealthFundSelect = true;
            else {
                isShowPrivateHealthFundSelect = false;
                searchPrivateHealthFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(16))) {
                isShowWorkCoverAuthoritySelect = true;

                await loadWorkCoverAuthorityFund();
            } else {
                isShowWorkCoverAuthoritySelect = false;
                searchWorkCoverAuthorityFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(17))) {
                isShowTrafficAccidentSelect = true;

                await loadTrafficAccidentFund();
            } else {
                isShowTrafficAccidentSelect = false;
                searchTrafficAccidentFundArr = [];
            }
        }

        requireReloadFunction && handleFilterSelect();
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleHealthFundTypeChange = (searchResult) => {
        searchPrivateHealthFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleWorkCoverAuthorityTypeChange = (searchResult = []) => {
        searchWorkCoverAuthorityFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleTrafficAccidentAuthorityTypeChange = (searchResult = []) => {
        searchTrafficAccidentFundArr = [...searchResult];

        handleFilterSelect();
    };

    const _handlePatientEncounters = async (
        res,
        isCsvContent = false,
        isReload = false,
    ) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load encounters, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            let result = isReload ? postEncounterData : [];

            if (!isCsvContent) {
                postEncounterData = [...res.results];
                count = res.total;
            }

            await loadInsuranceFund();

            await cacheWorkersCompensation();

            await cacheTrafficAccidentAuthorities();

            await tick();

            postEncounterData =
                await getEncounterWithWcAndTacDetails(postEncounterData);

            result = await _mapNameOfInsurer(postEncounterData);

            result = await syncEncounterLatestNote(result);

            if (isCsvContent) return result;
            else postEncounterData = [...result];
        }
    };

    const _mapNameOfInsurer = (arr = []) =>
        arr.map((encounter) => ({
            ...encounter,
            nameOfInsurer: getNameOfInsurer(encounter),
        }));

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    const { sort } = params;
                    const title = row.childNodes[0].data;

                    if (title == "Encounter ID") {
                        if (sort == "id") row.appendChild(upSpan);
                        else if (sort == "idDesc") row.appendChild(downSpan);
                    } else if (title == "Doctor") {
                        if (sort == "doctor") row.appendChild(upSpan);
                        else if (sort == "doctorDesc")
                            row.appendChild(downSpan);
                    } else if (title == "Scheduled Date") {
                        if (sort == "scheduledDate") row.appendChild(upSpan);
                        else if (sort == "scheduledDateDesc")
                            row.appendChild(downSpan);
                    } else if (title == "Invoice Due Date") {
                        if (sort == "invoiceDate") row.appendChild(upSpan);
                        else if (sort == "invoiceDateDesc")
                            row.appendChild(downSpan);
                    }
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = shallowCopy(ConsentDueFilter);

        if (filters.sort) params.sort = filters.sort;

        if (filters.encounterId) params["filter[id]"] = filters.encounterId;

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        if (searchInsuranceTypeArr.length)
            params["filter[patientInsuranceType]"] =
                searchInsuranceTypeArr.join(",");

        if (searchPrivateHealthFundArr.length)
            params["filter[patientInsuranceID]"] =
                searchPrivateHealthFundArr.join(",");

        if (searchWorkCoverAuthorityFundArr.length)
            params["filter[patientWorkCoverAuthority]"] =
                searchWorkCoverAuthorityFundArr.join(",");

        if (searchTrafficAccidentFundArr.length)
            params["filter[patientTrafficAccidentAuthority]"] =
                searchTrafficAccidentFundArr.join(",");

        if (filters.dateFrom)
            params["filter[scheduledDateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[scheduledDateTo]"] = filters.dateTo;

        switch (filters.consentType) {
            case 2:
                params["filter[estimaterejected]"] = 1;
                delete params["filter[estimateignoredrejected]"];
                delete params["filter[estimateignored]"];
                break;
            case 3:
                params["filter[estimateignored]"] = 1;
                delete params["filter[estimateignoredrejected]"];
                delete params["filter[estimaterejected]"];
                break;
            case 1:
            default:
                params["filter[estimateignoredrejected]"] = 1;
                delete params["filter[estimateignored]"];
                delete params["filter[estimaterejected]"];
                filters.consentType = 1;
                break;
        }

        if (filters.commsDisabled) params["filter[disableNotifications]"] = 1;

        if (!isNaN(filters.badDebtor) && Number(filters.badDebtor) >= 0)
            params["filter[badDebtor]"] = filters.badDebtor;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;
            if (params["filter[id]"])
                str += `&encounterId=${params["filter[id]"]}`;
            if (params["filter[doctorId]"])
                str += `&doctorId=${params["filter[doctorId]"]}`;
            if (params["filter[patientInsuranceType]"])
                str += `&patientInsuranceType=${params["filter[patientInsuranceType]"]}`;
            if (params["filter[patientInsuranceID]"])
                str += `&patientInsuranceID=${params["filter[patientInsuranceID]"]}`;
            if (params["filter[patientWorkCoverAuthority]"])
                str += `&workCoverAuthorityID=${params["filter[patientWorkCoverAuthority]"]}`;
            if (params["filter[patientTrafficAccidentAuthority]"])
                str += `&trafficAccidentAuthorityID=${params["filter[patientTrafficAccidentAuthority]"]}`;
            if (params["filter[scheduledDateFrom]"])
                str += `&fromDate=${params["filter[scheduledDateFrom]"]}`;
            if (params["filter[scheduledDateTo]"])
                str += `&toDate=${params["filter[scheduledDateTo]"]}`;
            if (params["filter[disableNotifications]"])
                str += `&commsDisabled=1`;
            if (
                !isNaN(params["filter[badDebtor]"]) &&
                Number(params["filter[badDebtor]"]) >= 0
            )
                str += `&badDebtor=${params["filter[badDebtor]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _getInvoiceId = (invoices = [], isCsvContent = false) => {
        if (!invoices.length) return "-";

        const result = [];
        let component;

        invoices.map((invoice) => {
            component = isCsvContent
                ? `${invoice.invoiceNumber}`
                : `<div class="text-secondary cursor-pointer hover:underline invoice${invoice.id}">${invoice.invoiceNumber}</div>`;

            result.push(component);
        });

        return result
            .toString()
            .replaceAll(",", isCsvContent ? "\r\n" : "<br>");
    };

    const _getInvoiceDueDate = (v) => {
        if (!v.invoices?.length) return "-";
        const firstValidInv = v.invoices.find((inv) => inv.status !== 9);

        if (firstValidInv && firstValidInv.dueDate)
            return getFormatDate(firstValidInv.dueDate);
        return "-";
    };

    const _getDoctorName = (doctor = {}, isCsvContent = false) => {
        if (isObjectEmpty(doctor)) return "-";

        const { fullName, id } = doctor;

        return isCsvContent
            ? `${fullName}`
            : `<div class="text-secondary cursor-pointer hover:underline doctor${id}">${fullName}</div>`;
    };

    const _getScheduleDate = (date) => {
        if (!date) return "-";

        const dateArr = date.split("T")[0].split("-");

        if (Number(dateArr[0]) < 1900) return "-";
        return getFormatDateAndTime(date);
    };

    const _getInsuranceType = (patient = {}) => {
        if (isObjectEmpty(patient)) return "-";

        const { insuranceType } = patient;

        return getInsuranceType(insuranceType);
    };

    const _renderActionIcons = (hasNotes = false) => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="viewIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white ${hasNotes ? "" : "hidden"}" data-tip="View all notes">
                        <i class="viewIcon ${eyeIcon}"></i>
                    </div>
                    <div class="notesIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Add note">
                        <i class="notesIcon ${addIcon}"></i>
                    </div>
                </div>`;
    };

    const _updateInsuranceFundsOptions = () => {
        healthFundOptionsArr = $insuranceFund.map((fund) => ({
            key: fund.id,
            value: fund.name,
        }));
    };

    const _updateWorkCoverOptions = () => {
        workCoverAuthorityOptionsArr = $workersCompensation.map((wc) => ({
            key: wc.id,
            value: wc.name,
        }));
    };

    const _updateTrafficAccidentOptions = () => {
        trafficAccidentOptionsArr = $trafficAccidentAuthorities.map((tac) => ({
            key: tac.id,
            value: tac.name,
        }));
    };

    const _openEditNotesModal = (encounter = {}) => {
        selectedEncounter = { ...encounter };
        isEditNotesModalOpen = true;
    };

    const _closeEditNotesModal = () => {
        selectedEncounter = {};
        isEditNotesModalOpen = false;
    };

    const _openViewNotesModal = (encounter = {}) => {
        selectedEncounter = { ...encounter };
        isViewNotesModalOpen = true;
    };

    const _closeViewNotesModal = (hasSelectedNotesChanged = false) => {
        selectedEncounter = {};
        isViewNotesModalOpen = false;

        hasSelectedNotesChanged &&
            loadPatientEncounters(
                page * pageSize,
                pageSize,
                _getSearchParams(),
            );
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div
        class="flex flex-0 items-start {$isMobile
            ? 'flex-col-reverse gap-y-4'
            : 'justify-between'}"
    >
        <div class="flex items-end justify-start flex-wrap gap-4">
            <SearchSelect
                title="Consent Status"
                isDisabled={isLoadingEncounter}
                bind:value={filters.consentType}
                isLong
                onChangeFunc={handleFilterSelect}
            >
                {#each EncounterConsentType as et}
                    <option
                        value={et.id}
                        selected={et.id === filters.consentType}
                    >
                        {et.type}
                    </option>
                {/each}
            </SearchSelect>

            <SearchInput
                bind:value={filters.encounterId}
                title="Encounter ID"
                type="number"
                inputFunc={() => {
                    if (!isNaN(filters.encounterId)) handleFilterSelect();
                }}
                id="encounterIdInput"
            />

            <DefaultDoctorFilter
                bind:filter={filters}
                filterPropertyName={"doctorId"}
                onChangeFunc={() => {
                    selectedDoctors = filters["doctorId"].split(",");
                    handleFilterSelect();
                }}
            />

            <DoctorMultiSearchSelect
                bind:searchResult={selectedDoctors}
                onChangeFunc={handleSelectDoctor}
                inputClass="w-32"
                zIndex="3"
            />

            {#if readyToShowMultiFilters}
                <MultiSearchSelect
                    title="Insurance Type"
                    inputID="patientInsuranceType"
                    isDisabled={isLoadingEncounter}
                    optionsArr={InsuranceType}
                    bind:searchResult={searchInsuranceTypeArr}
                    onChangeFunc={handleInsuranceTypeChange}
                    inputClass="w-32"
                />

                {#if isShowPrivateHealthFundSelect}
                    <MultiSearchSelect
                        title="Private Health Fund"
                        inputID="patientHealthFundType"
                        isDisabled={isLoadingEncounter}
                        optionsArr={healthFundOptionsArr}
                        bind:searchResult={searchPrivateHealthFundArr}
                        onChangeFunc={handleHealthFundTypeChange}
                        inputClass="w-32"
                    />
                {/if}

                {#if isShowWorkCoverAuthoritySelect}
                    <MultiSearchSelect
                        title="Work Cover Authority"
                        inputID="consentDueWorkCoverAuthority"
                        isDisabled={isLoadingWorkCoverAuthorityFund}
                        optionsArr={workCoverAuthorityOptionsArr}
                        bind:searchResult={searchWorkCoverAuthorityFundArr}
                        onChangeFunc={handleWorkCoverAuthorityTypeChange}
                        inputClass="w-32"
                    />
                {/if}

                {#if isShowTrafficAccidentSelect}
                    <MultiSearchSelect
                        title="Traffic Accident Authority"
                        inputID="consentDueTrafficAccidentAuthority"
                        isDisabled={isLoadingTrafficAccidentAuthority}
                        optionsArr={trafficAccidentOptionsArr}
                        bind:searchResult={searchTrafficAccidentFundArr}
                        onChangeFunc={handleTrafficAccidentAuthorityTypeChange}
                        inputClass="w-32"
                    />
                {/if}
            {/if}

            {#if !$user.settings?.disableDateRangeInput}
                <SearchDateRangeInput
                    bind:startDate={filters.dateFrom}
                    bind:endDate={filters.dateTo}
                    title="Date Range"
                    inputClass="borderB w-[272px]"
                    on:change={handleFilterSelect}
                    showClearButton
                    disable={isLoadingEncounter}
                    showScrollYearOptions
                />
            {:else}
                <SearchInput
                    bind:value={filters.dateFrom}
                    title="Date from"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isLoadingEncounter}
                    maxDate={filters.dateTo ?? undefined}
                    showScrollYearOptions
                />

                <SearchInput
                    bind:value={filters.dateTo}
                    title="Date to"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isLoadingEncounter}
                    minDate={filters.dateFrom ?? undefined}
                    showScrollYearOptions
                />
            {/if}

            <SearchSelect
                bind:value={filters.badDebtor}
                title="Bad Debtor"
                onChangeFunc={handleFilterSelect}
                isDisabled={isLoadingEncounter}
            >
                <option value={Number(-1)}>All</option>
                <option value={Number(0)} selected>Non-Bad Debtors</option>
                <option value={Number(1)}> Bad Debtors </option>
            </SearchSelect>

            <div class="flex flex-row gap-x-[6px]">
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={filters.commsDisabled}
                    id="disabledComms"
                    on:change={handleFilterSelect}
                    disabled={isLoadingEncounter}
                />
                <label for="disabledComms">Comms Disabled</label>
            </div>

            <ResetFilterLink
                bind:filter={filters}
                defaultFilter={{
                    consentType: ConsentDueFilter["filter[consent]"],
                    dateFrom: defaultFilter["filter[scheduleDateFrom]"],
                    dateTo: defaultFilter["filter[scheduleDateTo]"],
                }}
                specialVariables={{
                    searchInsuranceTypeArr,
                    searchPrivateHealthFundArr,
                }}
                onResetFilter={() => {
                    searchInsuranceTypeArr = [];
                    handleInsuranceTypeChange(searchInsuranceTypeArr, {
                        requireReloadFunction: false,
                    });
                    searchPrivateHealthFundArr = [];

                    clearFilterDoctor();
                }}
            />
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoading}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                bind:this={scrollTableContainerRef}
            >
                <SvelteTable
                    {columns}
                    rows={postEncounterData}
                    customRow="align-middle"
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={postEncounterData}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: postEncounterData,
            fileName: "Consent Due",
            allDataApi: async () => {
                let res = await apiMethods.getMethod.getPatientEncounter(
                    0,
                    999999,
                    _getSearchParams(),
                );

                const results = await _handlePatientEncounters(res, true);

                return { results };
            },
        }}
    />
</div>

{#if isEditNotesModalOpen}
    <EditNotesModal
        isModalOpen={isEditNotesModalOpen}
        encounter={selectedEncounter}
        confirmAction={() =>
            loadPatientEncounters(
                page * pageSize,
                pageSize,
                _getSearchParams(),
            )}
        closeModal={_closeEditNotesModal}
    />
{/if}

{#if isViewNotesModalOpen}
    <ViewNotesModal
        isModalOpen={isViewNotesModalOpen}
        encounter={selectedEncounter}
        closeModal={_closeViewNotesModal}
    />
{/if}
